// Port of backend/src/utils/checklistCycle.js - the client has to agree with the server
// about which day a repeating checklist comes round on, or the list you are looking at
// and the reminder you get would be answering different questions. Kept deliberately
// small: the server owns the reset itself (it is the one that can see the clock of every
// device you own), this only decides what to show and what to call it.
export const REPEAT_UNITS = ['day', 'week', 'month'];
// Minutes. Mirrors REPEAT_INTERVAL_CHOICES on the server, which rejects anything else.
export const REPEAT_INTERVAL_CHOICES = [30, 60, 120, 180, 240, 360, 480, 720];

export function isRepeating(reminderType) {
  return reminderType === 'daily' || reminderType === 'every';
}

function toDate(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ''));
  if (!match) return null;
  // UTC noon: these are wall-clock dates, and noon keeps the arithmetic clear of DST.
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
}

function toISO(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function addDays(date, days) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

// Clamps to the end of a short month, like every calendar app does.
function addMonths(date, months) {
  const day = date.getUTCDate();
  const next = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, 12));
  const daysInMonth = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0, 12)).getUTCDate();
  next.setUTCDate(Math.min(day, daysInMonth));
  return next;
}

export function cycleStart(checklist, todayString) {
  if (!isRepeating(checklist.reminderType)) return null;
  if (checklist.reminderType === 'daily') return todayString;

  const anchor = toDate(checklist.date);
  const today = toDate(todayString);
  if (!anchor || !today || today.getTime() < anchor.getTime()) return null;

  const every = Math.max(1, Number(checklist.repeatEvery) || 1);
  if (checklist.repeatUnit === 'month') {
    const elapsedMonths = (today.getUTCFullYear() - anchor.getUTCFullYear()) * 12 + (today.getUTCMonth() - anchor.getUTCMonth());
    let periods = Math.floor(elapsedMonths / every);
    let candidate = addMonths(anchor, periods * every);
    if (candidate.getTime() > today.getTime()) {
      periods -= 1;
      if (periods < 0) return null;
      candidate = addMonths(anchor, periods * every);
    }
    return toISO(candidate);
  }

  const stepDays = every * (checklist.repeatUnit === 'week' ? 7 : 1);
  const elapsedDays = Math.round((today.getTime() - anchor.getTime()) / 86400000);
  return toISO(addDays(anchor, Math.floor(elapsedDays / stepDays) * stepDays));
}

// The days this checklist belongs on: every day for 'daily', the cycle boundary for
// 'every', its own date for 'once'.
export function isActiveOn(checklist, dateString) {
  if (!isRepeating(checklist.reminderType)) return checklist.date === dateString;
  return cycleStart(checklist, dateString) === dateString;
}
